import React from 'react';
import * as Y from 'yjs';
import {
    CollectionContent,
    CollectionProperty,
    findYArrayIndex,
    SafeCollectionContent,
    SpaceOperations,
} from '@saga/shared';
import { RealtimeStateWithDocument } from '@/components/RealtimeProvider';
import {
    useDocumentProperty,
    usePerformActionWithSafeContent as usePerformActionWithSafeContentBase,
    RealtimeDocumentPovider as BaseRealtimeDocumentPovider,
    RealtimeDocumentContextType,
} from './BaseRealtimeDocumentProvider';
import { v4 as uuidv4 } from 'uuid';
import { useCurrentWorkspace } from './WorkspaceContext';
import { useSpace } from './SpaceProvider';

export type CollectionPropertyValue = Omit<CollectionProperty, 'values'> & {
    value: string | undefined;
};

export const RealtimeCollectionContext = React.createContext<RealtimeDocumentContextType<SafeCollectionContent>>({
    loadDocument: () => Promise.resolve(undefined),
    unLoadDocument: () => {},
    documentStatesMap: {},
});
RealtimeCollectionContext.displayName = 'Collection Provider';

function safeCollectionFromYMapConverter(state: RealtimeStateWithDocument) {
    return SpaceOperations.safeCollectionContentFromYMap(state.document.getMap('collection'));
}

export function useCollectionProperties(collectionId: string) {
    const [trigger, forceUpdate] = React.useReducer(() => ({}), {});
    const { space } = useSpace();

    const { data, type } = useDocumentProperty<Y.Array<CollectionProperty>, CollectionContent>(
        collectionId,
        'properties',
        safeCollectionFromYMapConverter,
        RealtimeCollectionContext,
    );

    const deleteNonExistingDocuments = React.useCallback(() => {
        const ypages = space?.map?.get('pages') as Y.Array<Y.Map<unknown>>;
        const ytasks = space?.map?.get('tasks') as Y.Array<Y.Map<unknown>>;

        const pageIds = ypages?.map((page) => page.get('id')) ?? [];
        const taskIds = ytasks?.map((task) => task.get('id')) ?? [];

        data?.forEach((property, index) => {
            if (Object.keys(property.values).some((id) => !pageIds.includes(id) && !taskIds.includes(id))) {
                data?.delete(index);
                data?.insert(index, [
                    {
                        ...property,
                        values: Object.fromEntries(
                            Object.entries(property.values).filter(
                                ([id]) => pageIds.includes(id) || taskIds.includes(id),
                            ),
                        ),
                    },
                ]);
            }
        });
    }, [data, space]);

    React.useEffect(() => {
        const ypages = space?.map?.get('pages') as Y.Array<Y.Map<unknown>>;
        const ytasks = space?.map?.get('tasks') as Y.Array<Y.Map<unknown>>;

        deleteNonExistingDocuments();

        ypages?.observe(deleteNonExistingDocuments);
        ytasks?.observe(deleteNonExistingDocuments);

        return () => {
            ypages?.unobserve(deleteNonExistingDocuments);
            ytasks?.unobserve(deleteNonExistingDocuments);
        };
    }, [space, deleteNonExistingDocuments]);

    React.useEffect(() => {
        data?.observe(forceUpdate);
        return () => {
            data?.unobserve(forceUpdate);
        };
    }, [data, forceUpdate]);

    const deps = React.useMemo(() => {
        trigger;
        return {
            properties: data?.toJSON() as CollectionProperty[] | null,
            type,
        };
    }, [data, trigger, type]);

    return deps;
}

export function useModifyCollectionProperties(collectionId: string) {
    const { data } = useDocumentProperty<Y.Array<CollectionProperty>, CollectionContent>(
        collectionId,
        'properties',
        safeCollectionFromYMapConverter,
        RealtimeCollectionContext,
    );

    const addProperty = React.useCallback(
        (property: Omit<CollectionProperty, 'id' | 'values'>): string => {
            const id = uuidv4();
            data?.push([{ ...property, id, values: {} }]);

            return id;
        },
        [data],
    );

    const removeProperty = React.useCallback(
        (propertyId: string) => {
            const index = data?.toJSON().findIndex((property) => property.id === propertyId);
            if (index != null && index !== -1) {
                data?.delete(index);
            }
        },
        [data],
    );

    const editProperty = React.useCallback(
        (propertyId: string, property: Partial<Omit<CollectionProperty, 'id'>>) => {
            const existingProperty = data?.toJSON().find((property) => property.id === propertyId);
            const index = data?.toJSON().findIndex((property) => property.id === propertyId);

            if (existingProperty && index != null && index !== -1) {
                data?.delete(index);
                data?.insert(index, [{ ...existingProperty, ...property }]);
            }
        },
        [data],
    );

    const setValueForDocumentProperty = React.useCallback(
        (documentId: string, propertyId: string, value: string | undefined) => {
            if (!data) return;
            const propertyIndex = findYArrayIndex(data, (property) => property.id === propertyId);

            if (propertyIndex !== -1) {
                const property = data?.get(propertyIndex);

                if (property) {
                    property.values[documentId] = value;
                    data?.delete(propertyIndex);
                    data?.insert(propertyIndex, [property]);
                }
            }
        },
        [data],
    );

    return React.useMemo(
        () => ({ addProperty, removeProperty, setValueForDocumentProperty, editProperty }),
        [addProperty, removeProperty, setValueForDocumentProperty, editProperty],
    );
}

export function usePerformActionWithSafeCollectionContent() {
    return usePerformActionWithSafeContentBase<SafeCollectionContent>(RealtimeCollectionContext);
}

export const RealtimeCollectionProvider = ({ children }: { children: React.ReactNode }) => {
    const { id } = useCurrentWorkspace();

    return (
        <BaseRealtimeDocumentPovider
            spaceId={id}
            converter={safeCollectionFromYMapConverter}
            documentType="collection"
            Context={RealtimeCollectionContext}
        >
            {children}
        </BaseRealtimeDocumentPovider>
    );
};
